import React, { useMemo, useEffect } from "react";
import {
  TravelerSelectWorkflow,
  TravelerSelectStep,
  ActionButton,
} from "halifax";
import { PersonId, SELECT_TRAVELERS } from "redmond";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import {
  ContactSelectors,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@capone/checkout";

import "./styles.scss";
import { useIsTravelApp } from "@capone/common";
import * as constants from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { config } from "../../../../api/config";
import { personToIPerson } from "../../../../utils/personToIPerson";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import { DesktopActivityBookValidationError } from "../DesktopActivityBookWorkflow";
import { ActivityTravelerSelectors } from "../../../../checkout";
import { PriceBreakdown } from "../PriceBreakdown";
import {
  AVAILABLE,
  CHECKOUT_DEFAULT_COMM_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

export interface PrimaryTravelerSelectionProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopActivityBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopActivityBookValidationError[]
  ) => void;
  showNonModalContent?: boolean;
  isXmodRedesign?: boolean;
}

export const PrimaryTravelerSelection = ({
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
  showNonModalContent,
  isXmodRedesign,
}: PrimaryTravelerSelectionProps) => {
  const [, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const { isTravelApp } = useIsTravelApp();

  const travelers = useSelector(ActivityTravelerSelectors.getUserTravelers);

  const selectedTravelerIds = useSelector(
    ActivityTravelerSelectors.getSelectedTravelerIds
  );

  const selectedPrimaryTravelerId = useSelector(
    ActivityTravelerSelectors.getSelectedPrimaryTravelerId
  );

  const getTravelerTypeByAge = useSelector(
    ActivityTravelerSelectors.getTravelerTypeByAge
  );

  const accountEmail = useSelector(ContactSelectors.getEmailAddress);

  const expState = useExperiments();
  const isDefaultCommEnabled =
    getExperimentVariant(expState.experiments, CHECKOUT_DEFAULT_COMM_EXPERIMENT) ===
    AVAILABLE;

  const selectableTravelers = useMemo(
    () =>
      travelers
        ?.filter((person) => selectedTravelerIds?.includes(person.id))
        .map(personToIPerson),
    [travelers, selectedTravelerIds]
  );

  const handleSelectTraveler = (travelerId?: PersonId) => {
    send({
      type: Event.SELECT_TRAVELER,
      travelerId,
      singleTravelerWorkflow: true,
    });
  };

  const canContinue = isMobile || !!selectedPrimaryTravelerId;

  const handleContinue = () => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else {
      setValidationErrorTypes?.(["primary-traveler"]);
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  useEffect(() => {
    if (!selectedPrimaryTravelerId && selectableTravelers.length === 1) {
      handleSelectTraveler(selectableTravelers[0].id);
    }
  }, [selectedPrimaryTravelerId, selectableTravelers]);

  useEffect(() => {
    if (selectedPrimaryTravelerId && selectableTravelers.length) {
      // reset primary traveler if not in selected travelers
      if (
        !selectableTravelers.some(
          (passenger) => passenger.id === selectedPrimaryTravelerId
        )
      ) {
        handleSelectTraveler(undefined);
      }
    }
  }, [selectableTravelers, selectedPrimaryTravelerId]);

  useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  const primaryTravelerSubtitle = isDefaultCommEnabled && accountEmail
    ? constants.VOUCHER_EMAIL_NOTICE
    : constants.PRIMARY_TRAVELER_SUBTITLE;

  return (
    <>
      <TravelerSelectWorkflow
        readOnly
        hideTravelerActions
        displayModalSubtitle
        showGenderField
        showNationalityField
        singleTravelerWorkflow
        buttonClassName="b2b"
        className={clsx("activity-primary-traveler-selection-root", {
          error: validationErrorTypes?.includes("primary-traveler"),
          xmod: isXmodRedesign,
        })}
        errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
        isMobile={isMobile}
        progress={
          isMobile ? TravelerSelectStep.TravelerSelect : TravelerSelectStep.Main
        }
        setProgress={() => {}}
        selectedTravelerIds={
          selectedPrimaryTravelerId ? [selectedPrimaryTravelerId] : []
        }
        setSelectedTravelerIds={(travelerIds: string[]) => {
          trackEvent({
            eventName: SELECT_TRAVELERS,
            properties: {},
          });
          handleSelectTraveler(travelerIds[0]);
        }}
        titles={{
          travelerInfoTitle: isMobile
            ? constants.TRAVELER_INFO_TEXT
            : constants.PRIMARY_TRAVELER_TITLE_SUBHEADING,
          travelerInfoSubtitle: isMobile
            ? constants.PRIMARY_TRAVELER_TITLE_SUBHEADING
            : primaryTravelerSubtitle,
          frequentFlyerTitle: "",
          additionalInfoTitle: "",
          adultTitle: constants.ADULT_TITLE,
          childTitle: constants.CHILD_TITLE,
          infantSeatTitle: "",
          infantLapTitle: "",
        }}
        travelers={selectableTravelers}
        tenant={config.TENANT}
        trackEvent={trackEvent}
        onContinue={handleContinue}
        onGoBack={handleBack}
        mobileTravelerRowType="checkbox"
        selectionScreenHeaderElement={<PriceBreakdown isMobile dropdown />}
        customHeader={
          <Box className="activity-traveler-mobile-header">
            <Typography className="pax-primary-traveler-heading" variant="h3">
              {constants.PRIMARY_TRAVELER_HEADING}
            </Typography>
            <hr />

            <Typography variant="h4" className="pax-header-secondary-title">
              {constants.PRIMARY_TRAVELER_TITLE_SUBHEADING}
            </Typography>
            <Typography className="pax-header-subtitle">
              {primaryTravelerSubtitle}
            </Typography>
          </Box>
        }
        showMobilePopoverTransition={false}
        bypassSelectForSingleTraveler={false}
        showNonModalContent={showNonModalContent}
        getTravelerTypeByAge={(age: number) => {
          const ageBand = getTravelerTypeByAge(age);
          return isDefaultCommEnabled && accountEmail ? `${ageBand}\n${accountEmail}` : ageBand;
        }}
        hideBackButton={isTravelApp}
      />
      {!isMobile && (
        <>
          <Typography
            className={clsx("activity-primary-traveler-select-notice", {
              error: validationErrorTypes?.includes("primary-traveler"),
            })}
          >
            {constants.PRIMARY_TRAVELER_NOTICE}
          </Typography>
          <ActionButton
            onClick={handleContinue}
            message={constants.CONTINUE_TEXT}
            className="activity-continue-button"
          />
        </>
      )}
    </>
  );
};
